// Twitter Integration Registration
// Registers Twitter integration with the integration registry

import { IntegrationRegistry } from '../registry/IntegrationRegistry';
import { TwitterIntegrationFactory } from './TwitterIntegrationFactory';
import { IntegrationConfig } from '../../../types/integration';

/**
 * Register Twitter integration with the integration registry
 */
export function registerTwitterIntegration(): void {
  const registry = IntegrationRegistry.getInstance();
  
  // Create a factory that implements the IntegrationFactory interface
  const twitterFactory = {
    async create(config: IntegrationConfig) {
      return await TwitterIntegrationFactory.create(config as any);
    },
    
    async validate(config: Partial<IntegrationConfig>) {
      if (!config.credentials) {
        return {
          valid: false,
          errors: [{ field: 'credentials', message: 'Credentials are required', code: 'REQUIRED' }],
          warnings: []
        };
      }
      
      try {
        const credentials = JSON.parse((config.credentials as any).encrypted);
        const testResult = await TwitterIntegrationFactory.testCredentials(credentials);
        
        if (testResult.success) {
          return {
            valid: true,
            errors: [],
            warnings: []
          };
        } else {
          return {
            valid: false,
            errors: [{ field: 'credentials', message: testResult.message, code: 'INVALID' }],
            warnings: []
          };
        }
      } catch (error) {
        return {
          valid: false,
          errors: [{ field: 'credentials', message: 'Invalid credentials format', code: 'INVALID' }],
          warnings: []
        };
      }
    },
    
    getConfigSchema() {
      return {
        credentials: {
          bearerToken: {
            type: 'string',
            required: false,
            description: 'Twitter API v2 Bearer Token (recommended)',
            sensitive: true
          },
          apiKey: {
            type: 'string',
            required: true,
            description: 'Twitter API Key (Consumer Key)'
          },
          apiSecret: {
            type: 'string',
            required: true,
            description: 'Twitter API Secret (Consumer Secret)',
            sensitive: true
          },
          accessToken: {
            type: 'string',
            required: false,
            description: 'OAuth 1.0a Access Token (for posting)',
            sensitive: true
          },
          accessTokenSecret: {
            type: 'string',
            required: false,
            description: 'OAuth 1.0a Access Token Secret (for posting)',
            sensitive: true
          }
        },
        settings: {
          enableTweeting: {
            type: 'boolean',
            default: true,
            description: 'Enable tweet publishing'
          },
          enableAnalytics: {
            type: 'boolean',
            default: true,
            description: 'Enable analytics and engagement tracking'
          },
          enableThreads: {
            type: 'boolean',
            default: true,
            description: 'Enable Twitter thread creation'
          },
          tweetSettings: {
            type: 'object',
            properties: {
              maxTweetLength: {
                type: 'number',
                default: 280,
                description: 'Maximum characters per tweet'
              },
              autoSplitLongContent: {
                type: 'boolean',
                default: true,
                description: 'Automatically split long content into threads'
              },
              addThreadNumbers: {
                type: 'boolean',
                default: true,
                description: 'Add numbers to thread tweets (1/n, 2/n, etc.)'
              }
            }
          },
          contentSettings: {
            type: 'object',
            properties: {
              maxHashtags: {
                type: 'number',
                default: 10,
                description: 'Maximum number of hashtags per tweet'
              },
              autoAddHashtags: {
                type: 'boolean',
                default: false,
                description: 'Automatically add relevant hashtags'
              },
              mentionNotifications: {
                type: 'boolean',
                default: true,
                description: 'Enable mention notifications'
              },
              linkShortening: {
                type: 'boolean',
                default: true,
                description: 'Use Twitter\'s automatic link shortening'
              }
            }
          },
          rateLimitSettings: {
            type: 'object',
            properties: {
              tweetsPerHour: {
                type: 'number',
                default: 50,
                description: 'Maximum tweets per hour'
              },
              respectRateLimits: {
                type: 'boolean',
                default: true,
                description: 'Automatically respect Twitter rate limits'
              },
              backoffOnLimit: {
                type: 'boolean',
                default: true,
                description: 'Back off when rate limits are hit'
              }
            }
          }
        }
      };
    },
    
    getSupportedFeatures() {
      return [
        'post_publishing',
        'analytics',
        'media_upload',
        'thread_posting',
        'engagement_tracking',
        'real_time_updates'
      ];
    }
  };

  // Register the integration
  registry.register(
    'social:twitter',
    'Twitter (X)',
    'social',
    'twitter',
    '1.0.0',
    twitterFactory
  );

  console.log('Twitter integration registered successfully');
}